/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */
import firebase from 'firebase/compat/app';

import { SplitData } from '@touchplan/common';

import {
	DefaultActionTypes,
	CombinedDefaultActions,
	AbortViewAction,
	ChangeViewAction,
	PersistDataAction,
	AddItemAction,
	SelectionDataLoadedAction,
	LocalUpdateAction,
} from './DefaultActionTypes';
import { makeLocalAction } from '../base/LocalSnapshot';
import { Observable, from } from 'rxjs';
import { FancyAction } from '../base/FancyAction';
import { FancyFirebaseList } from '../base/FancyFirebaseList';
import { FancyFirebaseObject } from '../base/FancyFirebaseObject';
import { logger } from '@touchplan/logging';

/**
 * A helper class to be used by a FancyFirebaseList or a FancyFirebaseObject
 * that includes a default set of handling for the default set of actions. To opt out
 * of default handling, pass in the excluded action types via the limiterList,
 * then implement your own version of the handling. These handlers conform to the types
 * defined in [[DefaultActionTypes]]. Every action here will be relevant to a FancyFirebaseList
 * not everything will be relevant to the object. If the divergence becomes great enough it might
 * be worth splitting into seperate classes.
 * @param list        The parent list that owns this class.
 * @param limiterList The exlude list for opting out of handling
 *
 * Usage
 *
 * ```
 * private defaultActionsHandlers:DefaultActionHandlers<Swimlane, FancyAction, SwimlaneList> = new DefaultActionHandlers(
 * 	this, [DefaultActionTypes.addItem]
 * );
 *
 * handleCustomEvents(baseObservable){
 * 	return baseObservable.pipe(
 * 		mergeMap((action:FancyAction)=>{
 * 			var item = this.defaultActionsHandlers.handle(action);
 * 			switch(action.type){
 * 				//override the add event
 * 				case DefaultActionTypes.addItem: item = this.handleAddItem(action);
 * 				default: break;
 * 			}
 * 			if(!item){ return empty(); }
 * 			return of(new FancyAction(item, action.type));
 * 		})
 * 	)
 * }
 * ```
 */

//enhancement, FancyModel's SplitData should be typed
export class DefaultActionHandlers<
	FancyModel extends SplitData<object, any>,
	FancyCustomAction extends FancyAction,
	ListOrObject extends FancyFirebaseList<FancyModel, FancyCustomAction> | FancyFirebaseObject<FancyModel>
> {
	constructor(public list: ListOrObject, limiterList: DefaultActionTypes[] = []) {
		this.limiterList = this.recalcLimiterListCache(limiterList);
	}

	private limiterList: Map<string, string>;
	private recalcLimiterListCache(limiterList: string[]) {
		const list = new Map();
		limiterList.forEach((key) => list.set(key, key));
		return list;
	}
	/**
	 * call with an action to result in a change.
	 * When implementing an override you can return a model to have it automatically wrapped in an action. Or an observable which is
	 * responsible for emitting n many actions. This is for cases which require more flexibility
	 */
	public handle(action: CombinedDefaultActions): FancyModel | Observable<FancyCustomAction> {
		if (this.skip(action.type)) {
			return null;
		}
		switch (action.type) {
			case DefaultActionTypes.addItem:
				return this.handleAddItem(action as AddItemAction<FancyModel>);
			case DefaultActionTypes.removeItem:
				return this.handleRemoveItem(action);
			case DefaultActionTypes.localUpdate:
				return this.handleLocalUpdate(action as LocalUpdateAction<FancyModel>);
			case DefaultActionTypes.changeView:
				return this.handleChangeView(action as ChangeViewAction<FancyModel>);
			case DefaultActionTypes.abortView:
				return this.handleAbortView(action as AbortViewAction<FancyModel>);
			case DefaultActionTypes.selectionDataLoaded:
				return this.handleSelectionLoaded(action as SelectionDataLoadedAction<FancyModel>);
			case DefaultActionTypes.persistData:
				return this.handlePersistData(action as PersistDataAction<FancyModel>);
			default:
				return null;
		}
	}
	private skip(fa: string) {
		return this.limiterList && this.limiterList.has(fa);
	}

	public handleAddItem(action: AddItemAction<FancyModel>) {
		if (this.list instanceof FancyFirebaseList) {
			const key = action.idOverride || this.list.generateNewChildKey();

			const addAction = makeLocalAction(action.payload, key, action.type);

			const item: FancyModel = this.list.$$added(addAction);
			this.list._internalList.set(key, item);
			item.localDataNotYetPersisted = true;

			if (action.persistImmediately) {
				return this.alsoPersistUtil(item, key, DefaultActionTypes.addItem);
			}

			return item;
		} else {
			throw new Error('FancyFirebaseObject does not support add');
		}
	}

	public handleRemoveItem(action: CombinedDefaultActions<FancyModel>) {
		const key = this.list instanceof FancyFirebaseList ? action.key : undefined;
		if (this.list instanceof FancyFirebaseList) {
			//always remove it immediately (not the same as the previous behavior, remove is now synchronous instead of after a short delay)
			this.list.$$removed(action.payload);
			this.list.syncList.delete(action.key);

			//only actually remove it from firebase once it's been persisted
			if (!action.payload.localDataNotYetPersisted) {
				this.list.listRef.remove(action.key);
			}
		} else {
			this.list.listRef.remove();
		}
		return action.payload;
	}
	public handleLocalUpdate(action: LocalUpdateAction<FancyModel>) {
		let item: any;
		if (this.list instanceof FancyFirebaseList) {
			item = this.list.syncList.get(action.key);
			if (!item) {
				logger.error(
					'handleLocalUpdate:  FancyFirebaseList does not have element with key, "' + action.key + '"'
				);
			}
		} else if (this.list instanceof FancyFirebaseObject) {
			item = this.list._internalObject;
			if (!item) {
				logger.error('handleLocalUpdate:  FancyFirebaseObject _internalObject is ' + item);
			}
		}
		if (item) {
			item.localUpdate(action.payload, action.renderType);
		}
		return item;
	}
	public handleChangeView(action: ChangeViewAction<FancyModel>) {
		action.payload.switchViews(action.renderType);
		return action.payload;
	}
	public handleAbortView(action: AbortViewAction<FancyModel>) {
		action.payload.switchViews(action.renderType, true);
		return action.payload;
	}
	public handleSelectionLoaded(action: SelectionDataLoadedAction<FancyModel>) {
		action.payload.setSelectionLoaded(action.loaded);
		return action.payload;
	}
	public handlePersistData(action: PersistDataAction<FancyModel>) {
		const item = action.payload;
		// item.copyDataBetweenViews(item.currentRenderType, item.primaryRenderType);
		const dbOutput = item.generateDatabaseOutput(action.renderType);

		let ref: firebase.database.Reference;
		if (this.list instanceof FancyFirebaseList) {
			ref = this.list.listRef.query.ref.child(action.key);
		} else if (this.list instanceof FancyFirebaseObject) {
			ref = this.list.listRef.query.ref;
		}

		ref.update(dbOutput);
		item.localDataNotYetPersisted = false;
		return item;
	}

	/**
	 * utility method for when you want to do "something" then also persist after you do it.
	 * For the most part this is just to ease overriding the add method.
	 * A custom persist function can be passed in to manage the exact firebase mechanism of how it gets persisted for this particular action
	 */
	public alsoPersistUtil(
		item: FancyModel,
		itemId: string,
		actionType: DefaultActionTypes,
		customPersist?: () => FancyModel
	) {
		const persistAction = new FancyAction(item, DefaultActionTypes.persistData, itemId) as PersistDataAction;
		const persistItem = customPersist ? customPersist() : this.handlePersistData(persistAction);

		// return two actions, can't use the shorthand of returning the model
		const actions = [
			new FancyAction(item, actionType, itemId) as FancyCustomAction, //add
			new FancyAction(persistItem, DefaultActionTypes.persistData, itemId) as FancyCustomAction, //persist
		];

		item.localDataNotYetPersisted = false;
		return from(actions);
	}
}
